import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { Grid, Box, CircularProgress } from "@mui/material";
import ToyCard from "../product/ToyCard";
import { useDispatch, useSelector } from "react-redux";
import { fetchProducts, SORT_BY } from "../../redux/actions/productActions";
import { RootState } from "../../redux/store";
import { Product } from "../../redux/types/productTypes";

interface HomeToysDisplayProps {
  categorySelected: string;
}

const PAGE_SIZE = 18;

const HomeToysDisplay: React.FC<HomeToysDisplayProps> = ({
  categorySelected,
}) => {
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const dispatch = useDispatch();
  const { loading, productsData, error } = useSelector(
    (state: RootState) => state.products
  );
  const { products, totalRecords } = productsData;
  const isCategoryChangeRef = useRef(false);
  const previousCategoryRef = useRef<string>("");
  const isFetchingRef = useRef(false);
  const lastFetchedDocIdRef = useRef<string | null>(null);
  const hasMoreProductsRef = useRef(true);

  useEffect(() => {
    const isCategoryChange = previousCategoryRef.current !== categorySelected;
    previousCategoryRef.current = categorySelected;

    if (isCategoryChange) {
      // Clear products immediately when category changes to show loading state
      setAllProducts([]);
      isCategoryChangeRef.current = true;
      isFetchingRef.current = true;
      hasMoreProductsRef.current = true;
      lastFetchedDocIdRef.current = null;
      const sortBy =
        categorySelected === "latest-toys" ? SORT_BY.LATEST : SORT_BY.POPULAR;
      dispatch(fetchProducts(PAGE_SIZE, "", categorySelected, "", sortBy));
    }
  }, [categorySelected, dispatch]);

  useEffect(() => {
    // Only update when loading is false and we have products
    if (!loading && products.length > 0) {
      if (isCategoryChangeRef.current) {
        // Replace products when category changes - use a small delay to batch the update
        // This prevents multiple re-renders
        const timeoutId = setTimeout(() => {
          setAllProducts(products);
          isCategoryChangeRef.current = false;
          isFetchingRef.current = false;
          // Reset to null so pagination can work - we'll set it when we start pagination
          lastFetchedDocIdRef.current = null;
          // Check if we've loaded all products
          hasMoreProductsRef.current =
            products.length < (totalRecords || Infinity);
        }, 0);
        return () => clearTimeout(timeoutId);
      } else {
        // Append products for pagination
        setAllProducts((prev) => {
          // Prevent duplicates by checking documentId
          const existingIds = new Set(prev.map((p) => p.documentId));
          const newProducts = products.filter(
            (p) => !existingIds.has(p.documentId)
          );
          if (newProducts.length === 0) {
            // No new products - we've reached the end
            hasMoreProductsRef.current = false;
            isFetchingRef.current = false;
            return prev; // Return same reference to prevent re-render
          }

          const updatedProducts = [...prev, ...newProducts];
          // Check if we've loaded all products
          hasMoreProductsRef.current =
            updatedProducts.length < (totalRecords || Infinity);
          isFetchingRef.current = false;
          // Reset lastFetchedDocIdRef so we can fetch the next page
          // We only keep it set while fetching to prevent duplicate fetches
          if (newProducts.length > 0 && hasMoreProductsRef.current) {
            lastFetchedDocIdRef.current = null; // Reset so next scroll can fetch
          } else {
            // No more products, keep it set to prevent further fetches
            lastFetchedDocIdRef.current =
              updatedProducts[updatedProducts.length - 1]?.documentId || null;
          }
          return updatedProducts;
        });
      }
    } else if (!loading && products.length === 0) {
      // No more products to load
      hasMoreProductsRef.current = false;
      isFetchingRef.current = false;
      if (isCategoryChangeRef.current) {
        isCategoryChangeRef.current = false;
        setAllProducts([]);
      }
    }
  }, [products, loading, totalRecords]);

  const handleScroll = useCallback(() => {
    const scroller = document.scrollingElement || document.documentElement;
    const { scrollTop, scrollHeight, clientHeight } = scroller;

    // Check if we're near the bottom and should load more
    // Use 120px threshold for mobile to account for elastic scrolling and dynamic browser toolbars
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 120;
    const lastDocId = allProducts[allProducts.length - 1]?.documentId;

    // Use isFetchingRef instead of loading to prevent duplicate fetches
    // This keeps the scroll listener stable (not recreated on every loading change)
    if (
      isNearBottom &&
      allProducts.length > 0 &&
      !isFetchingRef.current &&
      !isCategoryChangeRef.current && // Don't paginate during category change
      hasMoreProductsRef.current &&
      lastDocId &&
      lastDocId !== lastFetchedDocIdRef.current
    ) {
      isFetchingRef.current = true;
      lastFetchedDocIdRef.current = lastDocId; // Track the cursor we're using
      // Use the correct sortBy based on the current category
      const sortBy =
        categorySelected === "latest-toys" ? SORT_BY.LATEST : SORT_BY.POPULAR;
      dispatch(
        fetchProducts(PAGE_SIZE, lastDocId, categorySelected, "", sortBy)
      );
    }
  }, [allProducts, dispatch, categorySelected]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Memoize the product list to prevent unnecessary re-renders of existing products
  // Don't include `loading` in dependencies - it causes all products to re-render on pagination
  // The bottom loading spinner handles pagination loading state separately
  const stableProducts = useMemo(() => {
    return allProducts.map((product) => ({
      product,
      loading: false, // Individual cards don't need loading state during pagination
    }));
  }, [allProducts]);

  return (
    <Box sx={{ position: "relative" }}>
      {/* Invisible anchor placed 32px above the visible section */}
      <Box
        id="products-section"
        sx={{
          position: "absolute",
          top: "-28px", // <-- offset amount
          height: "1px",
          width: "1px",
          pointerEvents: "none",
          visibility: "hidden",
        }}
      />

      {/* Visible section (no id here) */}
      <Box
        minHeight="1000px"
        bgcolor="#f0f0f0"
        padding="16px 0 32px"
        margin="0px"
      >
        <Grid container spacing={2} style={{ padding: 16 }}>
          {loading && (allProducts.length === 0 || isCategoryChangeRef.current)
            ? Array.from({ length: 1 }).map((_, index) => (
                <Grid item key={index} xs={12}>
                  <Box display="flex" justifyContent="center">
                    <CircularProgress />
                  </Box>
                </Grid>
              ))
            : stableProducts.map(({ product, loading: productLoading }) => (
                <Grid item key={product.documentId} xs={6} sm={4} md={3} lg={2}>
                  <ToyCard product={product} loading={productLoading} />
                </Grid>
              ))}

          {loading &&
            allProducts.length >= PAGE_SIZE &&
            hasMoreProductsRef.current && (
              <Grid item xs={12}>
                <Box display="flex" justifyContent="center" marginTop={2}>
                  <CircularProgress />
                </Box>
              </Grid>
            )}

          {error && (
            <Grid item xs={12}>
              <Box color="error.main" marginTop={2}>
                {error}
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default HomeToysDisplay;
